import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEOHead from './SEOHead';
import NarrationVoiceBrowser from './NarrationVoiceBrowser';
import { premiumVoices, PREMIUM_GENRES, PREMIUM_GENDERS, PREMIUM_ACCENTS } from '../data/premiumVoices';

const { FiMic, FiGlobe, FiCheck, FiArrowRight, FiSliders, FiShield, FiInfo, FiHeadphones } = FiIcons;

const voiceStyles = [
  { name: 'Warm literary', desc: 'Measured, intimate narration for literary fiction and memoir.', tag: 'Fiction' },
  { name: 'Bright & brisk', desc: 'Energetic, clear delivery for business, self-help, and how-to.', tag: 'Non-fiction' },
  { name: 'Cinematic', desc: 'Weightier tone with dynamic range for thrillers and fantasy.', tag: 'Genre' },
  { name: 'Friendly & gentle', desc: 'Soft, expressive reads for children’s books and YA.', tag: 'Younger readers' },
  { name: 'Documentary', desc: 'Grounded, credible narration for history and long-form non-fiction.', tag: 'Historical' },
  { name: 'Multilingual', desc: 'Natural narration for translated editions, consistent across languages.', tag: 'International' },
];

const pickSteps = [
  { icon: FiHeadphones, title: 'Audition against your book', desc: 'Press play on real sample narration, filtered by genre, voice, and accent, and hear how each narrator carries your kind of story.' },
  { icon: FiSliders, title: 'Tune the read', desc: 'Set pace, warmth, and emphasis, and lock in pronunciations for names and invented terms.' },
  { icon: FiMic, title: 'Narrate every chapter', desc: 'Your chosen narrator stays consistent from Chapter 1 to the closing credits — no drift between sessions.' },
];

const AudiobookVoicesPage = () => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Audiobook Narration Voices — ManuscriptSmith',
    description: 'Choose a natural AI narrator matched to your genre. Consistent narration across every chapter of your audiobook.',
    url: 'https://manuscriptsmith.com/audiobook/voices'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-parchment-50 to-parchment-100">
      <SEOHead
        title="Audiobook Narration Voices | ManuscriptSmith"
        description="Natural AI narrators for every genre. Consistent pacing, tone, and pronunciation across every chapter — mastered to ACX/Audible spec."
        keywords="ai audiobook narrator, ai narration voices, audiobook narration, natural ai voice, audiobook voice styles"
        canonicalUrl="https://manuscriptsmith.com/audiobook/voices"
        structuredData={structuredData}
      />

      <main>
        {/* Hero */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-10 text-center">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-primary-700 bg-primary-50 px-3 py-1 rounded-full mb-5">
            Audiobook · Narration voices
          </div>
          <motion.h1
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          >
            A narrator that fits{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">your book.</span>
          </motion.h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Pick a natural AI voice matched to your genre. The read stays consistent — same tone, pacing,
            and pronunciation from the first line to the last.
          </p>
        </section>

        {/* Voice styles grid */}
        <section className="bg-white py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-8">
              <SafeIcon icon={FiGlobe} className="w-7 h-7 text-primary-600" />
              <h2 className="text-3xl font-bold text-gray-900">Voices for every kind of book</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {voiceStyles.map((v, i) => (
                <motion.div
                  key={v.name}
                  className="rounded-2xl border border-parchment-200 p-6 bg-parchment-50"
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: (i % 3) * 0.08 }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-r from-primary-500 to-secondary-500 flex items-center justify-center">
                      <SafeIcon icon={FiMic} className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-[11px] font-bold uppercase tracking-wide text-primary-700 bg-white px-2.5 py-1 rounded-full">{v.tag}</span>
                  </div>
                  {/* mini waveform */}
                  <div className="flex items-end gap-0.5 h-8 mb-3">
                    {[...Array(28)].map((_, k) => (
                      <div key={k} className="w-1 rounded-full bg-gradient-to-t from-primary-400 to-secondary-300"
                        style={{ height: `${25 + ((k * (i + 2)) % 7) * 11}%` }} />
                    ))}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">{v.name}</h3>
                  <p className="text-sm text-gray-600">{v.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Playable voice samples — comprehensive narrator browser */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Listen before you decide</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Audition a full library of natural AI narrators. Filter by genre, voice, and accent, then press play to hear real sample narration — and match one to your book.</p>
          </div>
          <NarrationVoiceBrowser />
        </section>

        {/* Premium voices — the studio-grade tier, 12 hand-picked narrators */}
        <section className="bg-white py-16 border-y border-parchment-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-secondary-700 bg-secondary-50 px-3 py-1 rounded-full mb-4">
                Premium tier
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Premium voices</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Twelve hand-picked, studio-grade narrators for books that deserve a signature read —
                richer dynamics, finer emotional range, and the same chapter-to-chapter consistency.
                Press play to audition each one.
              </p>
            </div>
            <NarrationVoiceBrowser
              voices={premiumVoices}
              genres={PREMIUM_GENRES}
              genders={PREMIUM_GENDERS}
              accents={PREMIUM_ACCENTS}
            />
          </div>
        </section>

        {/* Cloned voices — coming soon; sets the right expectation w/o disclosing the pipeline */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="rounded-3xl border border-primary-200 bg-gradient-to-br from-primary-50 to-parchment-50 p-8 md:p-12">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-primary-700 bg-white px-3 py-1 rounded-full mb-5 shadow-sm">
              Coming soon
            </div>
            <div className="flex items-start gap-4 mb-5">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-primary-500 to-secondary-500 flex items-center justify-center flex-shrink-0">
                <SafeIcon icon={FiMic} className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Your own voice — narrated for you</h2>
                <p className="text-lg text-gray-600 max-w-3xl">
                  A cloned narration voice is coming to ManuscriptSmith, and it will be part of the offering.
                  Your book, read in a voice that sounds like you — with the consistency and polish of a studio narrator,
                  across every chapter.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-5 mt-8">
              <div className="bg-white/70 rounded-2xl border border-primary-100 p-6">
                <SafeIcon icon={FiGlobe} className="w-6 h-6 text-primary-600 mb-3" />
                <h3 className="text-base font-bold text-gray-900 mb-2">Your accent stays your accent</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  A cloned voice holds its own accent and character from the first line to the last — no drift between
                  sessions, no chapter that suddenly sounds like someone else.
                </p>
              </div>
              <div className="bg-white/70 rounded-2xl border border-primary-100 p-6">
                <SafeIcon icon={FiCheck} className="w-6 h-6 text-primary-600 mb-3" />
                <h3 className="text-base font-bold text-gray-900 mb-2">Names & places, pronounced right</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Foreign place names, invented terms, and unusual character names sometimes need special preparation to
                  narrate correctly. ManuscriptSmith handles that automatically in the background — so your read sounds
                  informed, not phonetic.
                </p>
              </div>
              <div className="bg-white/70 rounded-2xl border border-primary-100 p-6">
                <SafeIcon icon={FiSliders} className="w-6 h-6 text-primary-600 mb-3" />
                <h3 className="text-base font-bold text-gray-900 mb-2">Tune your narration profile</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  You stay in control: adjust pronunciations and fine-tune your narration profile so your cloned voice
                  reads every name, term, and passage exactly the way you intend.
                </p>
              </div>
            </div>

            <p className="text-sm text-gray-500 mt-8 flex items-start gap-2">
              <SafeIcon icon={FiInfo} className="w-4 h-4 mt-0.5 flex-shrink-0" />
              Cloned voices roll out to authors in a coming release. Until then, audition the studio and premium voices above —
              your book is ready to narrate today.
            </p>
          </div>
        </section>

        {/* How choosing works */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center gap-3 mb-3">
            <SafeIcon icon={FiHeadphones} className="w-7 h-7 text-primary-600" />
            <h2 className="text-3xl font-bold text-gray-900">Find the read that fits</h2>
          </div>
          <p className="text-gray-600 mb-10 max-w-3xl">
            The right narrator changes how a book lands. Audition, tune, and lock in a voice that stays
            consistent across the whole audiobook.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {pickSteps.map((s, i) => (
              <motion.div
                key={s.title}
                className="bg-white rounded-2xl border border-parchment-200 p-7"
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
              >
                <div className="w-14 h-14 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 flex items-center justify-center mb-5">
                  <SafeIcon icon={s.icon} className="w-7 h-7 text-white" />
                </div>
                <div className="text-sm font-bold text-gray-400 mb-1">STEP {String(i + 1).padStart(2, '0')}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{s.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Honesty */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="bg-parchment-100 border border-parchment-200 rounded-2xl p-8">
            <div className="flex items-start gap-3 mb-4">
              <SafeIcon icon={FiShield} className="w-6 h-6 text-primary-600 flex-shrink-0 mt-0.5" />
              <h2 className="text-2xl font-bold text-gray-900">Honest about voices and distribution</h2>
            </div>
            <ul className="space-y-3">
              {[
                'Every narrator here is a natural AI voice you can audition in full before committing.',
                'Your book and your Voice DNA — the private fingerprint of how you write — stay yours and are never used to train other authors’ output.',
                'Whichever voice you choose, files are mastered to ACX/Audible spec and ready for the sanctioned distribution paths.',
                'AI-narrated audio reaches Audible via Amazon’s Virtual Voice on KDP — we prep for that path and won’t overpromise a shortcut.'
              ].map((t) => (
                <li key={t} className="flex items-start">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-primary-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{t}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-gray-500 mt-5 flex items-start gap-2">
              <SafeIcon icon={FiInfo} className="w-4 h-4 mt-0.5 flex-shrink-0" />
              Prefer a human narrator? Your prepped chapter scripts, pronunciation guide, and voice samples hand off cleanly to one.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
          <div className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl p-10 text-center text-white shadow-xl">
            <h2 className="text-3xl font-bold mb-4">Hear your book in the right voice</h2>
            <p className="text-lg opacity-90 mb-8">Write it, then narrate it in a voice that fits your book.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/audiobook/acx" className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-600 font-bold text-lg rounded-lg hover:bg-gray-50 transition-all">
                See the ACX standard <SafeIcon icon={FiArrowRight} className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/signup" className="inline-flex items-center justify-center px-8 py-4 bg-white/15 border border-white/40 text-white font-semibold text-lg rounded-lg hover:bg-white/25 transition-all">
                Get started
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default AudiobookVoicesPage;
